<template>
  <nav class="flex-1 px-2 pb-4 space-y-8 mt-4">
    <!-- Top-level items (no group header) -->
    <div class="space-y-0.5">
      <template v-for="item in topLevelItems" :key="item.name">
        <router-link
          :to="item.to"
          class="flex items-center px-2 py-1.5 rounded-md group"
          :class="[
            isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
            'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
          ]"
          @click="$emit('close')"
        >
          <component
            :is="item.icon"
            class="h-4 w-4 mr-2"
            :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
            aria-hidden="true"
          />
          <span class="flex-1">{{ $t("Navigation." + item.name) }}</span>
          <span v-if="item.pulse" class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
        </router-link>
      </template>
    </div>

    <!-- Grouped sections -->
    <template v-for="group in navigationGroups" :key="group.key">
      <div v-if="group.items.length">
        <p class="px-2 mb-1 text-xs font-extralight text-gray-800 uppercase tracking-wider">
          {{ $t("Navigation." + group.key) }}
        </p>
        <div class="space-y-0.5">
          <template v-for="item in group.items" :key="item.name">
            <div>
              <!-- Parent navigation item with children -->
              <div
                v-if="item.children"
                @click="toggleItem(item.name)"
                class="flex items-center justify-between cursor-pointer px-2 py-1.5 rounded-md group"
                :class="[
                  isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
                  'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
                ]"
              >
                <div class="flex items-center space-x-2">
                  <component
                    :is="item.icon"
                    class="h-4 w-4"
                    :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
                    aria-hidden="true"
                  />
                  <span>{{ $t("Navigation." + item.name) }}</span>
                </div>
                <component
                  :is="isExpanded(item.name) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  class="h-4 w-4 text-gray-400 transition-transform duration-200"
                />
              </div>

              <!-- Simple navigation item -->
              <router-link
                v-else
                :to="item.to"
                class="flex items-center px-2 py-1.5 rounded-md group"
                :class="[
                  isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
                  'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
                ]"
                @click="$emit('close')"
              >
                <component
                  :is="item.icon"
                  class="h-4 w-4 mr-2"
                  :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
                  aria-hidden="true"
                />
                <span>{{ $t("Navigation." + item.name) }}</span>
              </router-link>

              <!-- Children navigation items -->
              <transition
                @before-enter="beforeEnter"
                @enter="enter"
                @after-enter="afterEnter"
                @before-leave="beforeLeave"
                @leave="leave"
                @after-leave="afterLeave"
              >
                <div
                  v-if="item.children && isExpanded(item.name)"
                  class="ml-5 border-l border-gray-200 pl-2 overflow-hidden"
                >
                  <router-link
                    v-for="child in item.children"
                    :key="child.name"
                    :to="child.to"
                    class="flex items-center px-3 py-1.5 text-sm rounded-md"
                    :class="[
                      isActiveRoute(child)
                        ? 'bg-primary-50 text-primary-dark font-medium'
                        : 'text-gray-500 hover:bg-gray-100',
                    ]"
                    @click="$emit('close')"
                  >
                    {{ $t("Navigation." + child.name) }}
                  </router-link>
                </div>
              </transition>
            </div>
          </template>
        </div>
      </div>
    </template>
  </nav>
</template>

<script>
import {
  DesktopComputerIcon,
  CollectionIcon,
  SparklesIcon,
  FireIcon,
  ArchiveIcon,
  ViewListIcon,
  UserCircleIcon,
  BookOpenIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClipboardCheckIcon,
  HomeIcon,
} from "@heroicons/vue/outline";
import { mapGetters, mapState } from "vuex";

export default {
  components: {
    DesktopComputerIcon,
    CollectionIcon,
    SparklesIcon,
    FireIcon,
    ArchiveIcon,
    ViewListIcon,
    UserCircleIcon,
    BookOpenIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ClipboardCheckIcon,
    HomeIcon,
  },

  data() {
    return {
      expandedItems: new Set(),
    };
  },

  mounted() {
    this.autoExpandActiveItem();
  },

  watch: {
    $route() {
      this.autoExpandActiveItem();
    },
  },

  computed: {
    ...mapState("auth", ["currentUser"]),
    ...mapGetters("auth", ["currentUserHasPermission"]),
    ...mapGetters("businessVerticals", ["hasExternalPosOnlySubscription"]),
    ...mapGetters("challenge", ["hasActiveChallenge"]),

    topLevelItems() {
      return [
        {
          name: "Home",
          to: "/pos/home",
          icon: HomeIcon,
        },
        this.hasExternalPosOnlySubscription
          ? null
          : {
              name: "Subscription",
              to: "/pos/subscription",
              icon: DesktopComputerIcon,
            },
        this.currentUserHasPermission("retributions")
          ? {
              name: "Retributions",
              to: "/pos/retributions",
              icon: SparklesIcon,
            }
          : null,
        this.hasActiveChallenge
          ? { name: "Challenge", to: "/pos/challenge/current", icon: FireIcon, pulse: true }
          : null,
      ].filter(Boolean);
    },

    navigationGroups() {
      return [
        {
          key: "group_management",
          items: [
            {
              name: "Plan sales",
              to: "/pos/plan-sales",
              icon: ArchiveIcon,
            },
            {
              name: "Claim List",
              to: "/pos/claims",
              icon: ViewListIcon,
              children: [
                {
                  name: "Claim management",
                  to: "/pos/claims",
                },
                {
                  name: "Claim invoices",
                  to: "/pos/claim_invoices",
                },
              ],
            },
            {
              name: "Leads",
              to: "/pos/leads",
              icon: CollectionIcon,
            },
          ],
        },
        {
          key: "group_settings",
          items: [
            {
              name: "Sellers",
              to: "/pos/sellers",
              icon: UserCircleIcon,
            },
            {
              name: "Offers",
              to: "/pos/contract-distributions",
              icon: ClipboardCheckIcon,
            },
            {
              name: "Training",
              to: "/pos/training",
              icon: BookOpenIcon,
            },
          ],
        },
      ];
    },
  },

  methods: {
    autoExpandActiveItem() {
      this.expandedItems.clear();
      for (const group of this.navigationGroups) {
        for (const item of group.items) {
          if (item.children && this.isActiveRoute(item)) {
            this.expandedItems.add(item.name);
            return;
          }
        }
      }
    },
    isActiveRoute(item) {
      if (item.to === this.$route.path) return true;
      if (this.$route.path.startsWith(item.to + "/")) return true;
      if (item.children) return item.children.some((child) => this.isActiveRoute(child));
      return false;
    },
    toggleItem(itemName) {
      if (this.expandedItems.has(itemName)) {
        this.expandedItems.delete(itemName);
      } else {
        this.expandedItems.clear();
        this.expandedItems.add(itemName);
      }
    },
    isExpanded(itemName) {
      return this.expandedItems.has(itemName);
    },
    // Transition hooks for smooth slide
    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
    },
    enter(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    afterEnter(el) {
      el.style.height = "auto";
      el.style.transition = "";
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    leave(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = "0";
      el.style.opacity = "0";
    },
    afterLeave(el) {
      el.style.transition = "";
      el.style.height = "auto";
    },
  },
};
</script>
